import { UsersRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { getUserManager } from '../auth/user-storage';
import { logger } from '../utils/logger';
import { userInfo } from 'os';

/**
 * Handle unified users management request
 * Supports operations: create, update, delete, getAll, getOne
 * Only accepts requests from 'admin' type
 *
 * Strategy: Try database collections first, fall back to file-based storage if failed
 */
export async function handleUsersRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      return null; // Collection not found, will fall back to file storage
    }
    return await handler(params);
  };

  try {
    const request = UsersRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;
    const numericId = requestData?.id;
    const username = requestData?.username;
    const email = requestData?.email;
    const password = requestData?.password;
    const fullname = requestData?.fullname;
    const role = requestData?.role;
    const userInfo = requestData?.userInfo;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;

    // Verify request is from admin
    if (from.type !== 'admin' && operation !== 'getOne' && operation !== 'getAll' && operation !== 'query') {
      sendResponse(id, {
        success: false,
        error: 'Unauthorized: Only admin can manage users'
      }, sendMessage, from.id);
      logger.warn(`Unauthorized user management attempt from: ${from.type}`);
      return;
    }

    const userManager = getUserManager();

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, { username, email, password, fullname, role, userInfo }, executeCollection, userManager, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, numericId, { username, email, password, fullname, role, userInfo }, executeCollection, userManager, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, numericId, username, executeCollection, userManager, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, userManager, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, numericId, username, executeCollection, userManager, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, userManager, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle users request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create user operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleCreate(
  id: string,
  userData: {
    username?: string;
    email?: string;
    password?: string;
    fullname?: string;
    role?: string;
    userInfo?: Record<string, unknown>;
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  const { username, email, password, fullname, role, userInfo } = userData;

  // Validate input - username is required
  if (!username || username.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Username is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!password || password.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Password is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  // Validate email format if provided
  if (email && email.trim().length > 0) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      sendResponse(id, {
        success: false,
        error: 'Invalid email format'
      }, sendMessage, clientId);
      return;
    }
  }

  // Try database collection first
  try {
    const result = await executeCollection('users', 'create', {
      username,
      email: email || undefined,
      password,
      fullname: fullname || undefined,
      role: role || undefined,
      userInfo: userInfo || undefined
    });

    if (result && result.success) {
      logger.info(`[DB] User created successfully: ${username}`);
      sendResponse(id, {
        success: true,
        data: {
          id: result.data?.id,
          username: result.data?.username,
          email: result.data?.email,
          fullname: result.data?.fullname,
          role: result.data?.role,
          message: `User '${username}' created successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to create user, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  try {
    // Check if user already exists
    if (userManager.userExists(username)) {
      sendResponse(id, {
        success: false,
        error: `User '${username}' already exists`
      }, sendMessage, clientId);
      return;
    }

    // Check if email already exists (if provided)
    if (email && userManager.getUserByEmail(email)) {
      sendResponse(id, {
        success: false,
        error: `User with email '${email}' already exists`
      }, sendMessage, clientId);
      return;
    }

    // Create user object
    const newUserData: any = {
      username,
      password,
    };

    if (email && email.trim().length > 0) {
      newUserData.email = email.trim();
    }

    if (fullname && fullname.trim().length > 0) {
      newUserData.fullname = fullname.trim();
    }

    if (role && role.trim().length > 0) {
      newUserData.role = role.trim();
    }

    // Create user
    const newUser = userManager.createUser(newUserData);

    logger.info(`[FILE] User '${username}' created successfully`);
    sendResponse(id, {
      success: true,
      data: {
        username: newUser.username,
        email: newUser.email,
        fullname: newUser.fullname,
        role: newUser.role,
        message: `User '${username}' created successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create user'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update user operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleUpdate(
  id: string,
  numericId: number | undefined,
  userData: {
    username?: string;
    email?: string;
    password?: string;
    fullname?: string;
    role?: string;
    userInfo?: Record<string, unknown>;
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  const { username, email, password, fullname, role, userInfo } = userData;

  // For DB update, we need numeric ID; for file storage, we need username
  if (!numericId && !username) {
    sendResponse(id, {
      success: false,
      error: 'User ID or username is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first (if numeric ID is provided)
  if (numericId) {
    try {
      const result = await executeCollection('users', 'update', {
        id: numericId,
        username,
        email,
        password,
        fullname,
        role,
        userInfo
      });

      if (result && result.success) {
        logger.info(`[DB] User updated successfully, ID: ${numericId}`);
        sendResponse(id, {
          success: true,
          data: {
            id: result.data?.id,
            username: result.data?.username,
            email: result.data?.email,
            fullname: result.data?.fullname,
            role: result.data?.role,
            message: `User updated successfully (DB)`
          }
        }, sendMessage, clientId);
        return;
      }
    } catch (dbError) {
      logger.warn(`[DB] Failed to update user, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
    }
  }

  // Fallback to file-based storage (requires username)
  if (!username || username.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Username is required for file-based storage update'
    }, sendMessage, clientId);
    return;
  }

  // Check if user exists
  if (!userManager.userExists(username)) {
    sendResponse(id, {
      success: false,
      error: `User '${username}' not found`
    }, sendMessage, clientId);
    return;
  }

  // Build update object
  const updates: any = {};

  // Validate and add email if provided
  if (email !== undefined) {
    if (email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        sendResponse(id, {
          success: false,
          error: 'Invalid email format'
        }, sendMessage, clientId);
        return;
      }

      // Check if email is already used by another user
      const existingUser = userManager.getUserByEmail(email);
      if (existingUser && existingUser.username !== username) {
        sendResponse(id, {
          success: false,
          error: `Email '${email}' is already used by another user`
        }, sendMessage, clientId);
        return;
      }

      updates.email = email.trim();
    } else {
      updates.email = undefined;
    }
  }

  if (password !== undefined && password.trim().length > 0) {
    updates.password = password.trim();
  }

  if (fullname !== undefined) {
    updates.fullname = fullname.trim().length > 0 ? fullname.trim() : undefined;
  }

  if (role !== undefined) {
    updates.role = role.trim().length > 0 ? role.trim() : undefined;
  }

  if (Object.keys(updates).length === 0) {
    sendResponse(id, {
      success: false,
      error: 'No fields to update. Please provide at least one field to update.'
    }, sendMessage, clientId);
    return;
  }

  try {
    const updatedUser = userManager.updateUser(username, updates);

    logger.info(`[FILE] User '${username}' updated successfully`);
    sendResponse(id, {
      success: true,
      data: {
        username: updatedUser.username,
        email: updatedUser.email,
        fullname: updatedUser.fullname,
        role: updatedUser.role,
        message: `User '${username}' updated successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update user'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete user operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleDelete(
  id: string,
  numericId: number | undefined,
  username: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // For DB delete, we need numeric ID; for file storage, we need username
  if (!numericId && !username) {
    sendResponse(id, {
      success: false,
      error: 'User ID or username is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first (if numeric ID is provided)
  if (numericId) {
    try {
      const result = await executeCollection('users', 'delete', { id: numericId });

      if (result && result.success) {
        logger.info(`[DB] User deleted successfully, ID: ${numericId}`);
        sendResponse(id, {
          success: true,
          data: {
            id: numericId,
            username: result.data?.username,
            message: `User deleted successfully (DB)`
          }
        }, sendMessage, clientId);
        return;
      }
    } catch (dbError) {
      logger.warn(`[DB] Failed to delete user, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
    }
  }

  // Fallback to file-based storage (requires username)
  if (!username || username.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Username is required for file-based storage delete'
    }, sendMessage, clientId);
    return;
  }

  // Check if user exists
  if (!userManager.userExists(username)) {
    sendResponse(id, {
      success: false,
      error: `User '${username}' not found`
    }, sendMessage, clientId);
    return;
  }

  const deleted = userManager.deleteUser(username);

  if (!deleted) {
    sendResponse(id, {
      success: false,
      error: `Failed to delete user '${username}'`
    }, sendMessage, clientId);
    return;
  }

  logger.info(`[FILE] User '${username}' deleted successfully`);
  sendResponse(id, {
    success: true,
    data: {
      username: username,
      message: `User '${username}' deleted successfully (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get all users operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection first
  try {
    const result = await executeCollection('users', 'getAll', {});

    if (result && result.success) {
      // Remove sensitive information like passwords
      const sanitizedUsers = result.data.map((user: any) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        fullname: user.fullname,
        role: user.role,
        userInfo : user.userInfo,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt
      }));

      logger.info(`[DB] Retrieved ${result.count} users`);
      sendResponse(id, {
        success: true,
        data: {
          users: sanitizedUsers,
          count: result.count,
          message: `Retrieved ${result.count} users (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to get all users, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  const users = userManager.getAllUsers();

  // Remove sensitive information like passwords
  const sanitizedUsers = users.map((user: any) => ({
    username: user.username,
    email: user.email,
    fullname: user.fullname,
    role: user.role,
    wsIds: user.wsIds || []
  }));

  logger.info(`[FILE] Retrieved ${sanitizedUsers.length} users`);
  sendResponse(id, {
    success: true,
    data: {
      users: sanitizedUsers,
      count: sanitizedUsers.length,
      message: `Retrieved ${sanitizedUsers.length} users (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get one user operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetOne(
  id: string,
  numericId: number | undefined,
  username: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // For DB getOne, we need numeric ID; for file storage, we need username
  if (!numericId && !username) {
    sendResponse(id, {
      success: false,
      error: 'User ID or username is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first (if numeric ID is provided)
  if (numericId) {
    try {
      const result = await executeCollection('users', 'getOne', { id: numericId });

      if (result && result.success) {
        // Remove sensitive information
        const sanitizedUser = {
          id: result.data?.id,
          username: result.data?.username,
          email: result.data?.email,
          fullname: result.data?.fullname,
          role: result.data?.role,
          userInfo: result.data?.userInfo,
          createdAt: result.data?.createdAt,
          updatedAt: result.data?.updatedAt
        };

        logger.info(`[DB] Retrieved user ID: ${numericId}`);
        sendResponse(id, {
          success: true,
          data: {
            user: sanitizedUser,
            message: `Retrieved user (DB)`
          }
        }, sendMessage, clientId);
        return;
      }
    } catch (dbError) {
      logger.warn(`[DB] Failed to get user, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
    }
  }

  // Fallback to file-based storage (requires username)
  if (!username || username.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Username is required for file-based storage lookup'
    }, sendMessage, clientId);
    return;
  }

  // Check if user exists
  if (!userManager.userExists(username)) {
    sendResponse(id, {
      success: false,
      error: `User '${username}' not found`
    }, sendMessage, clientId);
    return;
  }

  const user = userManager.getUser(username);

  // Remove sensitive information
  const sanitizedUser = {
    username: user.username,
    email: user.email,
    fullname: user.fullname,
    role: user.role,
    wsIds: user.wsIds || []
  };

  logger.info(`[FILE] Retrieved user: ${username}`);
  sendResponse(id, {
    success: true,
    data: {
      user: sanitizedUser,
      message: `Retrieved user '${username}' (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle query users operation with filters
 * Database only - no file fallback for query operations
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  userManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection
  try {
    const result = await executeCollection('users', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    if (result && result.success) {
      // Remove sensitive information like passwords
      const sanitizedUsers = result.data.map((user: any) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        fullname: user.fullname,
        role: user.role,
        userInfo: user.userInfo,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt
      }));

      logger.info(`[DB] Query returned ${result.count} users`);
      sendResponse(id, {
        success: true,
        data: {
          users: sanitizedUsers,
          count: result.count,
          message: `Query returned ${result.count} users (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to query users: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage (get all and return - no filter support)
  const users = userManager.getAllUsers();

  // Remove sensitive information like passwords
  const sanitizedUsers = users.map((user: any) => ({
    username: user.username,
    email: user.email,
    fullname: user.fullname,
    role: user.role,
    userInfo : user.userInfo,
    wsIds: user.wsIds || []
  }));

  logger.info(`[FILE] Retrieved ${sanitizedUsers.length} users (all - no query filter)`);
  sendResponse(id, {
    success: true,
    data: {
      users: sanitizedUsers,
      count: sanitizedUsers.length,
      message: `Retrieved ${sanitizedUsers.length} users (File - no query filter)`
    }
  }, sendMessage, clientId);
}

/**
 * Send users response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'USERS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
